import { DealEmail } from "@/types"

/** Patterns that mark the start of a quoted reply or signature in plain text */
const REPLY_MARKERS = [
  /^-{3,}\s*Original Message\s*-{3,}/i,
  /^_{10,}/,
  /^On .+ wrote:\s*$/i,
  /^From:\s.+/i,
  /^>/,
  /^Sent from my /i,
  /^Get Outlook for /i,
]

/** Strip quoted reply text from the plain-text bodyPreview (used in collapsed view) */
export function stripPreviewQuote(preview: string): string {
  return stripPlainTextQuote(preview)
}

/** Strip quoted replies and signatures from plain text */
function stripPlainTextQuote(text: string): string {
  const lines = text.split("\n")

  // Find the earliest reply/signature marker
  let cutIndex = -1
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    if (REPLY_MARKERS.some((rx) => rx.test(line.trim()))) {
      cutIndex = i
      break
    }
  }

  let result = cutIndex > 0
    ? lines.slice(0, cutIndex).join("\n")
    : text

  // Strip trailing signature: look backwards for phone/email/url-heavy block
  result = stripTrailingSignature(result)

  return result.trimEnd()
}

/** Remove signature block from the end of text content */
function stripTrailingSignature(text: string): string {
  const lines = text.split("\n")

  // Look for a "-- " or standalone name line followed by company/contact info
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i].trim()
    // Stop if we've gone more than 15 lines up (signatures aren't that long)
    if (lines.length - i > 15) break
    // Explicit signature separator
    if (line === "--" || line === "-- ") {
      return lines.slice(0, i).join("\n")
    }
  }

  // Heuristic: if the last chunk has phone numbers, emails, URLs, strip it
  // Walk backwards while lines look like contact/signature info
  let sigStart = lines.length
  for (let i = lines.length - 1; i >= Math.max(0, lines.length - 15); i--) {
    const line = lines[i].trim()
    if (!line) { sigStart = i; continue }
    const isContactLine =
      /\(\d{3}\)\s*\d{3}-\d{4}/.test(line) ||       // phone (xxx) xxx-xxxx
      /\d{3}[-.\s]\d{3}[-.\s]\d{4}/.test(line) ||    // phone xxx-xxx-xxxx
      /\b\w+@\w+\.\w+\b/.test(line) ||               // email
      /\bwww\.\S+/.test(line) ||                      // www URL
      /\bhttps?:\/\/\S+/.test(line) ||                // http URL
      /Connect with me|Book an appointment|Send me files|Subscribe/i.test(line) ||
      /LinkedIn|Calendly|Dropbox|Twitter|Facebook/i.test(line) ||
      /Info Videos/i.test(line)
    if (isContactLine) {
      sigStart = i
    } else if (sigStart < lines.length && lines.length - sigStart >= 3) {
      // We've found at least 3 contact-ish lines below; check if this line
      // could be name/company/address (short, no sentence structure)
      if (line.length < 60 && !line.endsWith(".") && !line.endsWith("?") && !line.endsWith("!")) {
        sigStart = i
      } else {
        break
      }
    } else {
      break
    }
  }

  if (sigStart < lines.length && lines.length - sigStart >= 3) {
    return lines.slice(0, sigStart).join("\n")
  }

  return text
}

/** Remove an element and all its following siblings from the DOM */
function removeFromElement(el: Element) {
  const toRemove: Element[] = []
  let sibling = el.nextElementSibling
  while (sibling) {
    toRemove.push(sibling)
    sibling = sibling.nextElementSibling
  }
  toRemove.forEach((node) => node.remove())
  el.remove()
}

/**
 * Walk every element in the doc; if its text content matches the Outlook
 * "From: ... Sent: ... To: ... Subject: ..." reply header, remove it and
 * everything after it in its parent (and all subsequent ancestor siblings).
 */
function removeOutlookReplyByText(doc: Document): boolean {
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT)
  let node = walker.nextNode() as Element | null
  while (node) {
    const text = (node.textContent || "").trim()
    // Match "From: ... Sent: ..." pattern (Outlook reply header)
    if (/^From:\s.+/i.test(text) && /Sent:\s/i.test(text) && /Subject:\s/i.test(text)) {
      // Walk up to find the right container to cut from
      let target: Element = node
      while (target.parentElement && target.parentElement !== doc.body) {
        const parentText = (target.parentElement.textContent || "").trim()
        // If the parent has significantly more text, stop — we want the tightest wrapper
        if (parentText.length > text.length + 50) break
        target = target.parentElement
      }
      removeFromElement(target)
      return true
    }
    node = walker.nextNode() as Element | null
  }
  return false
}

/** Clean an HTML or plain-text email body: strip quoted replies, signatures, footer images/links */
export function cleanEmailHtml(body: string, contentType: string): string {
  if (contentType === "html") {
    const parser = new DOMParser()
    const doc = parser.parseFromString(body, "text/html")

    // 1. Strip quoted replies — structural markers

    // Outlook: <div id="appendonsend">
    const appendOnSend = doc.getElementById("appendonsend")
    if (appendOnSend) removeFromElement(appendOnSend)

    // Outlook: <div id="divRplyFwdMsg"> (reply/forward header)
    const rplyFwd = doc.getElementById("divRplyFwdMsg")
    if (rplyFwd) removeFromElement(rplyFwd)

    // Outlook: div with border-top style (reply separator line)
    const borderDiv = doc.querySelector('div[style*="border-top"]')
    if (borderDiv) removeFromElement(borderDiv)

    // Outlook: first <hr> marks reply boundary
    const hrs = Array.from(doc.querySelectorAll("hr"))
    if (hrs.length > 0) removeFromElement(hrs[0])

    // Gmail: <div class="gmail_quote">
    doc.querySelector(".gmail_quote")?.remove()

    // Generic: <blockquote>
    doc.querySelectorAll("blockquote").forEach((bq) => bq.remove())

    // 2. Fallback: text-based Outlook "From: ... Sent: ..." detection
    removeOutlookReplyByText(doc)

    // 3. Strip signatures

    // Known signature container selectors
    const sigSelectors = [
      "#Signature", "#signature", ".Signature", ".signature",
      "#EmailSignature", ".email-signature", "[data-signature]",
    ]
    for (const sel of sigSelectors) {
      doc.querySelector(sel)?.remove()
    }

    // Remove all <img> tags (logos, tracking pixels, signature images)
    doc.querySelectorAll("img").forEach((img) => img.remove())

    // Remove trailing empty/link-heavy blocks
    const children = Array.from(doc.body.children)
    for (let i = children.length - 1; i >= 0; i--) {
      const el = children[i]
      const text = (el.textContent || "").trim()
      const links = el.querySelectorAll("a")
      if (!text || (links.length > 0 && text.length < links.length * 40)) {
        el.remove()
      } else {
        break
      }
    }

    // 4. Text-based signature stripping on remaining HTML text
    // Extract text, detect signature, then remove matching trailing elements
    const remainingText = (doc.body.textContent || "").trim()
    const strippedText = stripTrailingSignature(remainingText)
    if (strippedText.length < remainingText.length * 0.85) {
      // Significant chunk was signature — remove trailing elements
      const bodyChildren = Array.from(doc.body.children)
      const sigText = remainingText.slice(strippedText.length)
      for (let i = bodyChildren.length - 1; i >= 0; i--) {
        const elText = (bodyChildren[i].textContent || "").trim()
        if (sigText.includes(elText) && elText.length > 0) {
          bodyChildren[i].remove()
        } else {
          break
        }
      }
    }

    return doc.body.innerHTML
  }

  // Plain text
  const cleaned = stripPlainTextQuote(body)
  return `<pre style="white-space: pre-wrap">${cleaned}</pre>`
}

export function groupByConversation(emails: DealEmail[]) {
  const map = new Map<string, { conversationId: string; subject: string | null; messages: DealEmail[] }>()

  for (const email of emails) {
    const key = email.conversationId || email.graphMessageId
    if (!map.has(key)) {
      map.set(key, {
        conversationId: key,
        subject: email.subject,
        messages: [],
      })
    }
    map.get(key)!.messages.push(email)
  }

  return Array.from(map.values()).sort((a, b) => {
    const aLatest = a.messages[a.messages.length - 1].receivedAt
    const bLatest = b.messages[b.messages.length - 1].receivedAt
    return new Date(bLatest).getTime() - new Date(aLatest).getTime()
  })
}
